import { useQuery } from '@tanstack/react-query';

import { missingGrantReason, type RudderstackGrantSetId } from '../lib/rudderstack-grant-sets';
import { useOrpc } from './useOrpc';

/** The server caches the sibling read for 5 minutes, so a minute on the client costs nothing. */
const SIBLING_STATUS_STALE_MS = 60_000;

/**
 * The sibling status of the current workspace (ACT2-1124 LLD §5.9): its model, its banner, and whether audience and
 * activation actions are off. `null` while the first read is in flight or when it failed: no banner, actions on.
 */
export function useSiblingStatus() {
  const orpc = useOrpc();
  const { data } = useQuery({
    ...orpc.siblingWorkspace.status.queryOptions({ input: {} }),
    staleTime: SIBLING_STATUS_STALE_MS,
  });
  return data ?? null;
}

/**
 * The workspace model where a guess is not safe (ACT2-1108, the setup panel's invite step): `unknown` when the read
 * failed, with `retry`, instead of the "not a sibling" that `useIsSiblingWorkspace` falls back to.
 */
export function useSiblingModel():
  | { model: 'loading' }
  | { model: 'unknown'; retry: () => void; retrying: boolean }
  | { model: 'sibling' | 'other' } {
  const orpc = useOrpc();
  const query = useQuery({
    ...orpc.siblingWorkspace.status.queryOptions({ input: {} }),
    staleTime: SIBLING_STATUS_STALE_MS,
  });
  if (query.data) return { model: query.data.model === 'sibling' ? 'sibling' : 'other' };
  if (query.isError) return { model: 'unknown', retry: () => void query.refetch(), retrying: query.isFetching };
  return { model: 'loading' };
}

/**
 * Whether the current workspace is a sibling workspace (ACT2-1124): true or false once the status is read, null while
 * the first read is in flight. A failed read counts as not a sibling workspace, so the shared-key screens show.
 */
export function useIsSiblingWorkspace(): boolean | null {
  const orpc = useOrpc();
  const { data, status } = useQuery({
    ...orpc.siblingWorkspace.status.queryOptions({ input: {} }),
    staleTime: SIBLING_STATUS_STALE_MS,
  });
  if (data) return data.model === 'sibling';
  return status === 'error' ? false : null;
}

/**
 * The "actions off" signal for audience and activation controls: on for a disabled or gone sibling, with the
 * banner's text as the tooltip reason.
 */
export function useSiblingActionsOff(): { off: boolean; reason: string } {
  const status = useSiblingStatus();
  return status?.actionsOff ? { off: true, reason: status.banner ?? '' } : { off: false, reason: '' };
}

/**
 * True when RudderStack's grants decide this person's audience, activation and account writes (ACT2-1124 LLD §6,
 * F13a): a sibling workspace, outside a private seat. The server computes it with the same rule its own checks use,
 * and answers a refused write with 403 and the grant set, so these controls drop Lookout's role gates. False while
 * the first read is in flight or when it failed: the Lookout gate stays until the answer arrives.
 */
export function useRudderstackGrantsDecide(): boolean {
  return useSiblingStatus()?.grantsDecide === true;
}

/**
 * Why this person cannot make a write that needs `set` (ACT2-1124 LLD §6), or null when they can. Only where
 * RudderStack's grants decide and the person's own sets are known and lack `set`: RudderStack would refuse the write
 * with 403. Null while the first read is in flight, when it failed, or when the sets are unknown: the control stays
 * on, and RudderStack still decides.
 */
export function useMissingRudderstackGrant(set: RudderstackGrantSetId): string | null {
  const status = useSiblingStatus();
  if (status?.grantsDecide !== true || !status.viewerGrantSets) return null;
  return status.viewerGrantSets.includes(set) ? null : missingGrantReason(set);
}
